/**
 * A Docker Compose file read just enough for the Apps tab (no YAML library): the file's `services:` with each one's
 * name, image, whether it is built from the folder (`build:`) and its ports (`"8080:80"`, `- 3000`, the long syntax's
 * `published`/`target`, an inline `[ ... ]` list). Anything else in the file is skipped; a line it does not understand
 * is left out rather than failing. The names the preview-runner looks for (container-live/preview-runner README).
 */
export const COMPOSE_FILES = ['docker-compose.yml', 'docker-compose.yaml', 'compose.yml', 'compose.yaml'];

/** The compose file among a folder's file names, or null. */
export const composeFileIn = (names) => COMPOSE_FILES.find((file) => (names || []).includes(file)) || null;

const unquote = (value) => String(value ?? '').trim().replace(/^(['"])(.*)\1$/, '$2');
// A comment after a value (`image: x # pinned`), not a `#` inside quotes at the start of the value.
const stripComment = (line) => line.replace(/\s+#(?=[^'"]*$).*$/, '');
const inlineList = (value) => {
  const match = String(value).trim().match(/^\[(.*)\]$/);
  return match ? match[1].split(',').map(unquote).filter(Boolean) : null;
};
const portText = (port) => {
  if (typeof port === 'string') { return port; }
  if (port.published && port.target) { return `${port.published}:${port.target}`; }
  return String(port.target || port.published || '');
};

/** `[{ name, image, build, ports: ['8080:80'] }]` in the file's order; [] for a file without `services:`. */
export function parseCompose(text) {
  const services = [];
  let inServices = false;
  let nameIndent = null;
  let fieldIndent = null;
  let current = null;
  let key = null;
  for (const raw of String(text || '').replace(/\t/g, '  ').split(/\r?\n/)) {
    const trimmed = raw.trim();
    if (!trimmed || trimmed.startsWith('#') || trimmed === '---') { continue; }
    const line = stripComment(raw);
    const indent = line.match(/^ */)[0].length;
    const body = line.trim();
    if (indent === 0) {
      inServices = /^services:\s*$/.test(body);
      nameIndent = null;
      current = null;
      continue;
    }
    if (!inServices) { continue; }
    if (nameIndent === null) { nameIndent = indent; }
    if (indent < nameIndent) { continue; }
    if (indent === nameIndent) {
      const name = body.match(/^(["']?[\w.-]+["']?):/);
      current = name ? { name: unquote(name[1]), image: null, build: false, ports: [] } : null;
      if (current) { services.push(current); }
      fieldIndent = null;
      key = null;
      continue;
    }
    if (!current) { continue; }
    if (fieldIndent === null) { fieldIndent = indent; }
    if (indent === fieldIndent) {
      const field = body.match(/^([\w.-]+):\s*(.*)$/);
      key = field ? field[1] : null;
      const value = field ? field[2] : '';
      if (key === 'image' && value) { current.image = unquote(value); }
      if (key === 'build') { current.build = true; }
      if (key === 'ports' && value) { current.ports.push(...(inlineList(value) || [])); }
      continue;
    }
    if (key !== 'ports' || indent < fieldIndent) { continue; }
    const item = body.match(/^-\s*(.*)$/);
    const pair = (item ? item[1] : body).match(/^(published|target):\s*(.+)$/);
    if (pair) {
      if (item || typeof current.ports[current.ports.length - 1] !== 'object') { current.ports.push({}); }
      current.ports[current.ports.length - 1][pair[1]] = unquote(pair[2]);
    } else if (item && item[1] && !/^[A-Za-z_][\w-]*:(\s|$)/.test(item[1])) {
      current.ports.push(unquote(item[1]));
    } else if (item) {
      current.ports.push({});
    }
  }
  return services.map((service) => ({ ...service, ports: service.ports.map(portText).filter(Boolean) }));
}

/** Whether the file IS the app (the runner's rule without a service.json: at least one service has `build:`). */
export const composeBuildsApp = (services) => (services || []).some((service) => service.build);
